import { assign } from "lodash";
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { FormGroup, FormControl } from '@angular/forms';
import * as FlatModels from '@medsurf/flat-models';
import { ZodSharedValidator } from "@medsurf/flat-validators";
import { BaseQuestionComponent } from '../base-question/base-question.component';

@Component({
    selector: 'medsurf-quiz-mapping-question',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './mapping-question.component.html',
    styleUrls: ['./mapping-question.component.scss'],
    standalone: false
})
export class MappingQuestionComponent extends BaseQuestionComponent<FlatModels.QuestionEntityModels.MappingQuestion> {
  choices_expanded = false;
  solutions_expanded = false;
  override form = new FormGroup(
    {
      placeholder: new FormControl(''),
      content: new FormControl(''),
      explanation: new FormControl(''),
      solutions: new FormControl(['0'])
    },
    {
      validators: ZodSharedValidator.validate(
        FlatModels.QuestionEntityModels.MappingQuestionSchema.pick({
          placeholder: true,
          content: true,
          explanation: true,
          solutions: true
        })
      ),
      updateOn: 'change'
    }
  );

  override updateForm() {
    if (this.index !== undefined && this.question) {
      let update = false;
      if (this.question.placeholder !== this.form.value.placeholder
        || this.question.content !== this.form.value.content
        || this.question.explanation !== this.form.value.explanation
        || this.question.solutions !== this.form.value.solutions) {
        update = true;
      }
      if (update) {
        // Update Data
        this.form.reset(this.question);
        this.isSubmitted.set(false);
      }
    }
  }

  override onSubmit() {
    if (this.isSubmitted() || this.form.invalid || !this.question) {
      return;
    }
    this.isSubmitted.set(true);
    
    // Update Question
    const question = assign(this.question, this.form.value);
    this.slideAuthorFacade.requestUpdateQuestion(question, 'history.question_update').subscribe({
      error: (result) => {
        ZodSharedValidator.handleError(result, this.alertControlFacade, this.form);
        this.isSubmitted.set(false);
      },
      next: () => {
        this.isSubmitted.set(false);
      }
    });
  }

  //<editor-fold desc="Choices">

  trackByChoice(index: number,
                item: FlatModels.ChoiceEntityModels.Choice) {
    return item.id + item.order || index;
  }

  onChoiceMoved(question: FlatModels.QuestionEntityModels.QuestionEntityType,
                choices: FlatModels.ChoiceEntityModels.Choice[]) {
    this.slideAuthorFacade.requestRearrangeChoices(question, choices).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
    });
  }

  addChoice(question: FlatModels.QuestionEntityModels.QuestionEntityType) {
    // Add Choice
    this.slideAuthorFacade.requestAddChoice(question, 'history.choice_add').subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
      next: () => {
        this.choices_expanded = true;
      }
    });
  }

  //</editor-fold>

  //<editor-fold desc="Solutions">

  onSolutionChange(data: {index: number, value: string}) {
    let solutions = this.form.value.solutions;
    if (!solutions) {
      solutions = [];
    }
    solutions[data.index] = data.value;
    this.form.patchValue({ solutions });
    this.onSubmit();
  }
  
  onSolutionMoved(solutions: string[]) {
    this.form.patchValue({ solutions: structuredClone(solutions) });
    this.onSubmit();
  }

  addSolution(question: FlatModels.QuestionEntityModels.MappingQuestion) {
    // Update Solutions
    question.solutions.push('0');

    // Add Solution
    this.slideAuthorFacade.requestUpdateQuestion(question, 'history.solution_add').subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
      next: () => {
        this.solutions_expanded = true;
      }
    });
  }
}
